// =============================================================
// App — top-level: auth gate, tab switching, service-worker
// registration, push subscription, and the install hint.
// =============================================================

import { useEffect, useRef, useState } from 'react'
import { ClawdPet } from './components/ClawdPet'
import { GachaOverlay } from './components/GachaOverlay'
import { PinScreen } from './components/PinScreen'
import { TabBar, type TabKey } from './components/TabBar'
import { authFetch, checkAuthStatus } from './lib/auth'
import { readNativeUsageSnapshot, writeNativeUsageSnapshot, type NativeUsageSnapshot } from './lib/nativeUsage'
import { ChatPage } from './pages/ChatPage'
import { KohakuPage } from './pages/KohakuPage'
import { NativeChatPage } from './pages/NativeChatPage'
import { StatusPage } from './pages/StatusPage'
import { YuzuPage } from './pages/YuzuPage'

type AuthState = 'checking' | 'unlocked' | 'needs-pin'

export function App() {
  const [authState, setAuthState] = useState<AuthState>('checking')
  const [tab, setTab] = useState<TabKey>('status')
  const [gachaOpen, setGachaOpen] = useState(false)
  const [installHintVisible, setInstallHintVisible] = useState(false)
  const [nativeUsage, setNativeUsage] = useState<NativeUsageSnapshot | null>(() => readNativeUsageSnapshot())
  const swRegRef = useRef<ServiceWorkerRegistration | null>(null)
  const pushSubscribedRef = useRef(false)

  useEffect(() => {
    void checkAuthStatus().then((s) => {
      setAuthState(s === 'valid' ? 'unlocked' : 'needs-pin')
    })
  }, [])

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('/sw.js')
        .then((reg) => {
          swRegRef.current = reg
        })
        .catch((e) => {
          console.warn('[sw] registration failed:', e)
        })
    }

    const onBeforeInstall = (e: Event) => {
      e.preventDefault()
      ;(window as unknown as { deferredPrompt?: Event }).deferredPrompt = e
      setInstallHintVisible(true)
    }
    const onInstalled = () => {
      setInstallHintVisible(false)
      ;(window as unknown as { deferredPrompt?: unknown }).deferredPrompt = null
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstall)
    window.addEventListener('appinstalled', onInstalled)
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall)
      window.removeEventListener('appinstalled', onInstalled)
    }
  }, [])

  function handleNativeUsage(next: NativeUsageSnapshot) {
    setNativeUsage(next)
    writeNativeUsageSnapshot(next)
  }

  async function trySubscribePush() {
    if (pushSubscribedRef.current) return
    if (!('PushManager' in window) || !swRegRef.current) return
    if (typeof Notification === 'undefined') return
    if (Notification.permission === 'denied') return
    if (Notification.permission !== 'granted') {
      const perm = await Notification.requestPermission()
      if (perm !== 'granted') return
    }
    try {
      const r = await authFetch('/api/vapid-public-key')
      const { publicKey } = (await r.json()) as { publicKey?: string }
      if (!publicKey) return
      const sub = await swRegRef.current.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
      })
      await authFetch('/api/push-subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sub.toJSON()),
      })
      pushSubscribedRef.current = true
    } catch (e) {
      console.warn('[push] subscribe failed:', e)
    }
  }

  if (authState === 'checking') return null
  if (authState === 'needs-pin') return <PinScreen onUnlocked={() => setAuthState('unlocked')} />

  return (
    <>
      <StatusPage active={tab === 'status'} onOpenGacha={() => setGachaOpen(true)} onNavigateChat={() => setTab('chat')} />
      <ChatPage active={tab === 'chat'} onUserActivity={trySubscribePush} />
      <NativeChatPage active={tab === 'native'} onUserActivity={trySubscribePush} onUsage={handleNativeUsage} />
      <YuzuPage active={tab === 'yuzu'} />
      <KohakuPage active={tab === 'kohaku'} nativeUsage={nativeUsage} />
      <ClawdPet />
      <TabBar active={tab} onChange={setTab} />
      <GachaOverlay open={gachaOpen} onClose={() => setGachaOpen(false)} />
      <div className={`install-hint ${installHintVisible ? '' : 'hidden'}`}>点击分享 → 添加到主屏幕</div>
    </>
  )
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i)
  return outputArray
}
